import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const camposDisponiveis = [
  { label: "Paciente", value: "nome_lead" },
  { label: "Telefone 1", value: "telefone1" },
  { label: "Telefone 2", value: "telefone2" },
  { label: "Procedimento", value: "id_procedimento" },
  { label: "Potencial", value: "potencial" },
  { label: "Total Orçamento", value: "total_orcamento" },
  { label: "Total Contratado", value: "total_contratado" },
  { label: "Status", value: "status_agendamento" },
  { label: "Data Agendamento", value: "data_agendamento" },
  { label: "Negociação", value: "status_negociacao" },
  { label: "Data Lead", value: "data_lead" },
  { label: "Região", value: "id_regiao" },
  { label: "Captador", value: "usuario_criador_id" },
  { label: "Dentista", value: "id_dentista" },
  { label: "Fonte", value: "id_fonte_lead" },
  { label: "Campanha", value: "nome_campanha" },
];

const statusOpcoes = [
  "Agendado",
  "Reagendado",
  "Compareceu",
  "Faltou",
  "Pendente",
];
const potencialOpcoes = ["Alto", "Médio", "Baixo"];
const negociacaoOpcoes = [
  "Prospectando",
  "Negociando",
  "Pendente",
  "Fechado Parcial",
  "Fechado",
  "Perdido",
  "Migrou Concorrência",
];

const ordenacaoOpcoes = [
  { value: "nome_lead", label: "Paciente" },
  { value: "data_lead", label: "Data da Lead" },
  { value: "data_agendamento", label: "Data Agendamento" },
  { value: "status_negociacao", label: "Negociação" },
  { value: "status_agendamento", label: "Status" },
  { value: "total_orcamento", label: "Total Orçamento" },
  { value: "total_contratado", label: "Total Contratado" },
  { value: "usuario_criador_id", label: "Captador" },
  { value: "id_dentista", label: "Dentista" },
  { value: "id_regiao", label: "Região" },
  { value: "id_fonte_lead", label: "Fonte" },
  { value: "nome_campanha", label: "Campanha" },
];

function normalizeText(v) {
  return String(v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function isOnlyDigits(v) {
  return /^\d+$/.test(String(v || "").trim());
}

function firstFilledLabel(...values) {
  for (const value of values) {
    const s = String(value ?? "").trim();
    if (!s) continue;
    if (isOnlyDigits(s)) continue;
    return s;
  }
  return "";
}

function buildMap(list, labelKeys = []) {
  const map = new Map();
  (Array.isArray(list) ? list : []).forEach((item) => {
    const idCandidates = [
      item?.id,
      item?.value,
      item?.id_usuario,
      item?.usuario_id,
      item?.id_dentista,
      item?.dentista_id,
      item?.id_regiao,
      item?.regiao_id,
      item?.id_fonte_lead,
      item?.fonte_id,
      item?.id_fonte,
      item?.id_campanha,
      item?.campanha_id,
      item?.id_procedimento,
      item?.procedimento_id,
      item?.nome,
    ];
    const key = String(
      idCandidates.find((v) => String(v ?? "").trim()) ?? "",
    ).trim();
    if (!key) return;

    const label = firstFilledLabel(
      ...labelKeys.map((k) => item?.[k]),
      item?.nome,
      item?.nome_fantasia,
      item?.profissional,
      item?.regiao,
      item?.campanha,
      item?.campanha_nome,
      item?.nome_campanha,
      item?.fonte,
      item?.fonte_nome,
      item?.procedimento,
      item?.nome_procedimento,
      item?.captador,
      item?.captador_nome,
      item?.usuario_criador_nome,
    );

    if (label) map.set(key, label);
  });
  return map;
}

function optionsFromMap(map) {
  return Array.from(map.entries())
    .map(([value, label]) => ({ value: String(value), label: String(label) }))
    .filter((item) => item.value && item.label)
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

function mergeMaps(primary, fallbackPairs) {
  const next = new Map(primary);
  (fallbackPairs || []).forEach(([id, label]) => {
    const key = String(id ?? "").trim();
    const value = String(label ?? "").trim();
    if (!key || !value) return;
    if (!next.has(key)) next.set(key, value);
  });
  return next;
}

function formatDateBR(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  const head = s.length >= 10 ? s.slice(0, 10) : s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(head))
    return head.split("-").reverse().join("/");
  return s;
}

function dateKey(v) {
  const s = String(v || "").trim();
  if (!s) return 0;
  const head = s.length >= 10 ? s.slice(0, 10) : s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return Number(head.replace(/-/g, ""));
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(head)) {
    const [d, m, y] = head.split("/");
    return Number(`${y}${m}${d}`);
  }
  return 0;
}

function toMoneyNum(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const s = String(v)
    .replace(/\s|R\$\s?/g, "")
    .trim();
  if (/^[\d.]+,\d{2}$/.test(s)) {
    return Number(s.replace(/\./g, "").replace(",", "."));
  }
  const n = Number(s.replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function moneyBRL(v) {
  return Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function statusStyle(tipo, valor) {
  const norm = normalizeText(valor);
  const base = {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    border: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  };

  if (tipo === "potencial") {
    if (norm === "alto")
      return {
        ...base,
        background: "#fee2e2",
        color: "#b91c1c",
        borderColor: "#fecaca",
      };
    if (norm === "medio" || norm === "médio")
      return {
        ...base,
        background: "#fef3c7",
        color: "#92400e",
        borderColor: "#fde68a",
      };
    return {
      ...base,
      background: "#dcfce7",
      color: "#166534",
      borderColor: "#bbf7d0",
    };
  }

  if (tipo === "status") {
    if (norm === "agendado")
      return {
        ...base,
        background: "#dbeafe",
        color: "#1d4ed8",
        borderColor: "#bfdbfe",
      };
    if (norm === "reagendado")
      return {
        ...base,
        background: "#ede9fe",
        color: "#6d28d9",
        borderColor: "#ddd6fe",
      };
    if (norm === "compareceu")
      return {
        ...base,
        background: "#dcfce7",
        color: "#166534",
        borderColor: "#bbf7d0",
      };
    if (norm === "faltou")
      return {
        ...base,
        background: "#fee2e2",
        color: "#b91c1c",
        borderColor: "#fecaca",
      };
    return {
      ...base,
      background: "#f8fafc",
      color: "#475569",
      borderColor: "#e2e8f0",
    };
  }

  if (tipo === "negociacao") {
    if (norm === "prospectando")
      return {
        ...base,
        background: "#eff6ff",
        color: "#1d4ed8",
        borderColor: "#bfdbfe",
      };
    if (
      norm === "negociando" ||
      norm === "em negociacao" ||
      norm === "em negociação"
    ) {
      return {
        ...base,
        background: "#fef3c7",
        color: "#92400e",
        borderColor: "#fde68a",
      };
    }
    if (norm === "pendente")
      return {
        ...base,
        background: "#f8fafc",
        color: "#475569",
        borderColor: "#e2e8f0",
      };
    if (norm === "fechado parcial")
      return {
        ...base,
        background: "#d1fae5",
        color: "#047857",
        borderColor: "#a7f3d0",
      };
    if (norm === "fechado")
      return {
        ...base,
        background: "#dcfce7",
        color: "#166534",
        borderColor: "#bbf7d0",
      };
    if (norm === "perdido")
      return {
        ...base,
        background: "#fee2e2",
        color: "#b91c1c",
        borderColor: "#fecaca",
      };
    if (norm === "migrou concorrencia" || norm === "migrou concorrência") {
      return {
        ...base,
        background: "#f3e8ff",
        color: "#7e22ce",
        borderColor: "#e9d5ff",
      };
    }
  }

  return {
    ...base,
    background: "#f8fafc",
    color: "#334155",
    borderColor: "#e2e8f0",
  };
}

function DropdownFiltro({ titulo, cor, options, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    function onDocClick(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const resumo =
    selected.length === 0
      ? "Todos"
      : selected.length === 1
        ? options.find((o) => String(o.value) === String(selected[0]))?.label ||
          "1 selecionado"
        : `${selected.length} selecionados`;

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 170 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          height: 42,
          borderRadius: 10,
          border: `1px solid ${cor}55`,
          background: "#fff",
          boxShadow: "0 4px 12px rgba(37,99,235,0.06)",
          padding: "0 12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <span
          style={{ fontSize: 11, fontWeight: 800, color: cor, lineHeight: 1.1 }}
        >
          {titulo}
        </span>
        <span
          style={{
            fontSize: 12,
            color: selected.length ? "#0f172a" : "#64748b",
            lineHeight: 1.2,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {resumo}
        </span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: 48,
            left: 0,
            width: 320,
            maxWidth: "90vw",
            background: "#fff",
            border: "1px solid #dbe3ef",
            borderRadius: 14,
            boxShadow: "0 16px 32px rgba(15,23,42,0.14)",
            zIndex: 50,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "10px 12px",
              borderBottom: "1px solid #eef2f7",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
            }}
          >
            <strong style={{ color: cor, fontSize: 13 }}>{titulo}</strong>
            <button
              type="button"
              onClick={() => {
                onClear();
                setOpen(false);
              }}
              style={{
                border: "none",
                background: "transparent",
                color: "#64748b",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Limpar
            </button>
          </div>

          <div style={{ maxHeight: 280, overflowY: "auto", padding: 8 }}>
            {options.length === 0 ? (
              <div style={{ padding: 12, color: "#94a3b8", fontSize: 12 }}>
                Sem opções.
              </div>
            ) : (
              options.map((opt) => {
                const checked = selected.includes(String(opt.value));
                return (
                  <label
                    key={`${titulo}-${opt.value}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 10,
                      cursor: "pointer",
                      background: checked ? `${cor}14` : "#fff",
                      color: checked ? cor : "#334155",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      value={opt.value}
                      onChange={(e) => {
                        onToggle(e);
                        setOpen(false);
                      }}
                      style={{ accentColor: cor }}
                    />
                    <span>{opt.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LeadPrintPage() {
  const [leads, setLeads] = useState([]);
  const [procedimentos, setProcedimentos] = useState([]);
  const [dentistas, setDentistas] = useState([]);
  const [regioes, setRegioes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [fontes, setFontes] = useState([]);
  const [campanhas, setCampanhas] = useState([]);

  const [filtroProcedimento, setFiltroProcedimento] = useState([]);
  const [filtroPotencial, setFiltroPotencial] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState([]);
  const [filtroNegociacao, setFiltroNegociacao] = useState([]);
  const [filtroRegiao, setFiltroRegiao] = useState([]);
  const [filtroDentista, setFiltroDentista] = useState([]);
  const [filtroFonte, setFiltroFonte] = useState([]);
  const [filtroCaptador, setFiltroCaptador] = useState([]);
  const [filtroCampanha, setFiltroCampanha] = useState([]);
  const [camposSelecionados, setCamposSelecionados] = useState(
    camposDisponiveis.map((c) => c.value),
  );

  const [ordenarPor, setOrdenarPor] = useState("nome_lead");
  const [direcaoOrdem, setDirecaoOrdem] = useState("asc");

  const empresaAtiva = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("empresaAtiva") || "null");
    } catch {
      return null;
    }
  }, []);

  const nomeClinica =
    empresaAtiva?.nome_fantasia ||
    empresaAtiva?.nome ||
    "Clínica Mundo Sorriso";

  useEffect(() => {
    setLeads(JSON.parse(localStorage.getItem("leadsParaImpressao") || "[]"));
    setProcedimentos(
      JSON.parse(localStorage.getItem("procedimentosParaImpressao") || "[]"),
    );
    setDentistas(
      JSON.parse(localStorage.getItem("dentistasParaImpressao") || "[]"),
    );
    setRegioes(
      JSON.parse(localStorage.getItem("regioesParaImpressao") || "[]"),
    );
    setUsuarios(
      JSON.parse(localStorage.getItem("usuariosParaImpressao") || "[]"),
    );
    setFontes(JSON.parse(localStorage.getItem("fontesParaImpressao") || "[]"));
    setCampanhas(
      JSON.parse(localStorage.getItem("campanhasParaImpressao") || "[]"),
    );
  }, []);

  const procedimentosMap = useMemo(
    () =>
      mergeMaps(
        buildMap(procedimentos, ["nome"]),
        (leads || [])
          .map((lead) => [
            lead?.id_procedimento,
            firstFilledLabel(
              lead?.procedimento,
              lead?.nome_procedimento,
              lead?.procedimento_nome,
            ),
          ])
          .filter(([id, nome]) => id && nome),
      ),
    [procedimentos, leads],
  );

  const dentistasMap = useMemo(
    () =>
      mergeMaps(
        buildMap(dentistas, ["profissional", "nome"]),
        (leads || [])
          .map((lead) => [
            lead?.id_dentista ?? lead?.dentista_id,
            firstFilledLabel(
              lead?.dentista,
              lead?.dentista_nome,
              lead?.nome_dentista,
              lead?.profissional,
            ),
          ])
          .filter(([id, nome]) => id && nome),
      ),
    [dentistas, leads],
  );

  const regioesMap = useMemo(
    () =>
      mergeMaps(
        buildMap(regioes, ["regiao", "nome"]),
        (leads || [])
          .map((lead) => [
            lead?.id_regiao ?? lead?.regiao_id,
            firstFilledLabel(lead?.regiao, lead?.nome_regiao),
          ])
          .filter(([id, nome]) => id && nome),
      ),
    [regioes, leads],
  );

  const usuariosMap = useMemo(
    () =>
      mergeMaps(
        buildMap(usuarios, ["nome"]),
        (leads || [])
          .map((lead) => [
            lead?.usuario_criador_id ?? lead?.captador_id,
            firstFilledLabel(
              lead?.captador,
              lead?.captador_nome,
              lead?.usuario_criador_nome,
              lead?.nome_captador,
            ),
          ])
          .filter(([id, nome]) => id && nome),
      ),
    [usuarios, leads],
  );

  const fontesMap = useMemo(
    () =>
      mergeMaps(
        buildMap(fontes, ["nome"]),
        (leads || [])
          .map((lead) => [
            lead?.id_fonte_lead ?? lead?.id_fonte ?? lead?.fonte_id,
            firstFilledLabel(lead?.fonte, lead?.fonte_nome, lead?.nome_fonte),
          ])
          .filter(([id, nome]) => id && nome),
      ),
    [fontes, leads],
  );

  const campanhasMap = useMemo(
    () =>
      mergeMaps(
        buildMap(campanhas, ["nome", "campanha_nome", "nome_campanha"]),
        (leads || [])
          .map((lead) => [
            lead?.id_campanha ?? lead?.nome_campanha,
            firstFilledLabel(
              lead?.nome_campanha,
              lead?.campanha,
              lead?.campanha_nome,
            ),
          ])
          .filter(([id, nome]) => id && nome),
      ),
    [campanhas, leads],
  );

  const procedimentosOptions = useMemo(
    () => optionsFromMap(procedimentosMap),
    [procedimentosMap],
  );
  const dentistasOptions = useMemo(
    () => optionsFromMap(dentistasMap),
    [dentistasMap],
  );
  const regioesOptions = useMemo(
    () => optionsFromMap(regioesMap),
    [regioesMap],
  );
  const usuariosOptions = useMemo(
    () => optionsFromMap(usuariosMap),
    [usuariosMap],
  );
  const fontesOptions = useMemo(() => optionsFromMap(fontesMap), [fontesMap]);
  const campanhasOptions = useMemo(
    () => optionsFromMap(campanhasMap),
    [campanhasMap],
  );

  const resolveProcedimento = (lead) =>
    firstFilledLabel(
      procedimentosMap.get(String(lead?.id_procedimento ?? "")),
      lead?.procedimento,
      lead?.nome_procedimento,
      lead?.procedimento_nome,
    ) || "—";

  const resolveDentista = (lead) =>
    firstFilledLabel(
      dentistasMap.get(String(lead?.id_dentista ?? lead?.dentista_id ?? "")),
      lead?.dentista,
      lead?.dentista_nome,
      lead?.nome_dentista,
      lead?.profissional,
    ) || "—";

  const resolveRegiao = (lead) =>
    firstFilledLabel(
      regioesMap.get(String(lead?.id_regiao ?? lead?.regiao_id ?? "")),
      lead?.regiao,
      lead?.nome_regiao,
    ) || "—";

  const resolveCaptador = (lead) =>
    firstFilledLabel(
      usuariosMap.get(
        String(lead?.usuario_criador_id ?? lead?.captador_id ?? ""),
      ),
      lead?.captador,
      lead?.captador_nome,
      lead?.usuario_criador_nome,
      lead?.nome_captador,
    ) || "—";

  const resolveFonte = (lead) =>
    firstFilledLabel(
      fontesMap.get(
        String(lead?.id_fonte_lead ?? lead?.id_fonte ?? lead?.fonte_id ?? ""),
      ),
      lead?.fonte,
      lead?.fonte_nome,
      lead?.nome_fonte,
    ) || "—";

  const resolveCampanha = (lead) =>
    firstFilledLabel(
      campanhasMap.get(String(lead?.id_campanha ?? lead?.nome_campanha ?? "")),
      lead?.nome_campanha,
      lead?.campanha,
      lead?.campanha_nome,
    ) || "—";

  function handleFiltroMulti(e, filtro, setFiltro) {
    const { value, checked } = e.target;
    setFiltro(checked ? [...filtro, value] : filtro.filter((v) => v !== value));
  }

  function handleCampoChange(e) {
    const { value, checked } = e.target;
    setCamposSelecionados((prev) =>
      checked ? [...prev, value] : prev.filter((v) => v !== value),
    );
  }

  function hasSelected(selected, values) {
    if (!selected.length) return true;
    const normalizedValues = values
      .map((v) => normalizeText(v))
      .filter(Boolean);
    return selected.some((item) =>
      normalizedValues.includes(normalizeText(item)),
    );
  }

  const leadsFiltradas = useMemo(() => {
    return (leads || []).filter((lead) => {
      if (
        !hasSelected(filtroProcedimento, [
          lead?.id_procedimento,
          resolveProcedimento(lead),
        ])
      )
        return false;
      if (!hasSelected(filtroPotencial, [lead?.potencial])) return false;
      if (!hasSelected(filtroStatus, [lead?.status_agendamento])) return false;
      if (!hasSelected(filtroNegociacao, [lead?.status_negociacao]))
        return false;
      if (
        !hasSelected(filtroRegiao, [
          lead?.id_regiao,
          lead?.regiao_id,
          resolveRegiao(lead),
        ])
      )
        return false;
      if (
        !hasSelected(filtroDentista, [
          lead?.id_dentista,
          lead?.dentista_id,
          resolveDentista(lead),
        ])
      )
        return false;
      if (
        !hasSelected(filtroFonte, [
          lead?.id_fonte_lead,
          lead?.id_fonte,
          lead?.fonte_id,
          resolveFonte(lead),
        ])
      )
        return false;
      if (
        !hasSelected(filtroCaptador, [
          lead?.usuario_criador_id,
          lead?.captador_id,
          resolveCaptador(lead),
        ])
      )
        return false;
      if (
        !hasSelected(filtroCampanha, [
          lead?.id_campanha,
          lead?.nome_campanha,
          resolveCampanha(lead),
        ])
      )
        return false;
      return true;
    });
  }, [
    leads,
    filtroProcedimento,
    filtroPotencial,
    filtroStatus,
    filtroNegociacao,
    filtroRegiao,
    filtroDentista,
    filtroFonte,
    filtroCaptador,
    filtroCampanha,
    procedimentosMap,
    dentistasMap,
    regioesMap,
    usuariosMap,
    fontesMap,
    campanhasMap,
  ]);

  const leadsOrdenadas = useMemo(() => {
    const arr = [...leadsFiltradas];

    function sortValue(lead) {
      switch (ordenarPor) {
        case "nome_lead":
          return normalizeText(lead?.nome_lead);
        case "data_lead":
          return dateKey(lead?.data_lead);
        case "data_agendamento":
          return dateKey(lead?.data_agendamento);
        case "status_negociacao":
          return normalizeText(lead?.status_negociacao);
        case "status_agendamento":
          return normalizeText(lead?.status_agendamento);
        case "total_orcamento":
          return toMoneyNum(lead?.total_orcamento);
        case "total_contratado":
          return toMoneyNum(lead?.total_contratado);
        case "usuario_criador_id":
          return normalizeText(resolveCaptador(lead));
        case "id_dentista":
          return normalizeText(resolveDentista(lead));
        case "id_regiao":
          return normalizeText(resolveRegiao(lead));
        case "id_fonte_lead":
          return normalizeText(resolveFonte(lead));
        case "nome_campanha":
          return normalizeText(resolveCampanha(lead));
        default:
          return normalizeText(lead?.nome_lead);
      }
    }

    arr.sort((a, b) => {
      const va = sortValue(a);
      const vb = sortValue(b);
      if (typeof va === "number" && typeof vb === "number") {
        return direcaoOrdem === "asc" ? va - vb : vb - va;
      }
      const cmp = String(va).localeCompare(String(vb), "pt-BR");
      return direcaoOrdem === "asc" ? cmp : -cmp;
    });

    return arr;
  }, [leadsFiltradas, ordenarPor, direcaoOrdem]);

  const totalLeads = leadsOrdenadas.length;
  const totalOrcamento = leadsOrdenadas.reduce(
    (acc, lead) => acc + toMoneyNum(lead?.total_orcamento),
    0,
  );
  const totalContratado = leadsOrdenadas.reduce(
    (acc, lead) => acc + toMoneyNum(lead?.total_contratado),
    0,
  );

  function renderCampo(lead, campo) {
    switch (campo) {
      case "id_procedimento":
        return resolveProcedimento(lead);
      case "id_dentista":
        return resolveDentista(lead);
      case "id_regiao":
        return resolveRegiao(lead);
      case "usuario_criador_id":
        return resolveCaptador(lead);
      case "id_fonte_lead":
        return resolveFonte(lead);
      case "nome_campanha":
        return resolveCampanha(lead);
      case "total_orcamento":
      case "total_contratado":
        return moneyBRL(lead?.[campo]);
      case "data_lead":
      case "data_agendamento":
        return formatDateBR(lead?.[campo]);
      default:
        return lead?.[campo] || "—";
    }
  }

  function renderCell(lead, campo) {
    if (campo === "status_agendamento") {
      return (
        <span style={statusStyle("status", lead?.status_agendamento)}>
          {lead?.status_agendamento || "—"}
        </span>
      );
    }
    if (campo === "status_negociacao") {
      return (
        <span style={statusStyle("negociacao", lead?.status_negociacao)}>
          {lead?.status_negociacao || "—"}
        </span>
      );
    }
    if (campo === "potencial") {
      return (
        <span style={statusStyle("potencial", lead?.potencial)}>
          {lead?.potencial || "—"}
        </span>
      );
    }
    if (campo === "total_orcamento" || campo === "total_contratado") {
      return (
        <strong style={{ color: "#0f172a" }}>{renderCampo(lead, campo)}</strong>
      );
    }
    return renderCampo(lead, campo);
  }

  function exportarExcel() {
    const rows = leadsOrdenadas.map((lead) =>
      camposDisponiveis
        .filter((c) => camposSelecionados.includes(c.value))
        .reduce((acc, campo) => {
          acc[campo.label] = renderCampo(lead, campo.value);
          return acc;
        }, {}),
    );
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `leads_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }

  const filtrosExibidos = [
    {
      label: "Procedimento",
      value: filtroProcedimento,
      render: (arr) =>
        arr.map((id) => procedimentosMap.get(String(id)) || id).join(", "),
    },
    { label: "Potencial", value: filtroPotencial },
    { label: "Status", value: filtroStatus },
    { label: "Negociação", value: filtroNegociacao },
    {
      label: "Região",
      value: filtroRegiao,
      render: (arr) =>
        arr.map((id) => regioesMap.get(String(id)) || id).join(", "),
    },
    {
      label: "Dentista",
      value: filtroDentista,
      render: (arr) =>
        arr.map((id) => dentistasMap.get(String(id)) || id).join(", "),
    },
    {
      label: "Fonte",
      value: filtroFonte,
      render: (arr) =>
        arr.map((id) => fontesMap.get(String(id)) || id).join(", "),
    },
    {
      label: "Captador",
      value: filtroCaptador,
      render: (arr) =>
        arr.map((id) => usuariosMap.get(String(id)) || id).join(", "),
    },
    {
      label: "Campanha",
      value: filtroCampanha,
      render: (arr) =>
        arr.map((id) => campanhasMap.get(String(id)) || id).join(", "),
    },
  ].filter((f) => f.value.length);

  const printCss = `
    @media print {
      body * { visibility: hidden; }
      #print-area, #print-area * { visibility: visible; }
      #print-area { position: absolute; left: 0; top: 0; width: 100vw; }
      .no-print { display: none !important; }
      .totais-footer { display: table-footer-group !important; }
      .tabela-scroll { display: block !important; overflow: visible !important; max-height: none !important; }
      .lead-table thead th { background: #1d4ed8 !important; color: #fff !important; }
    }
    @media screen {
      .totais-footer { display: none; }
    }
    .lead-table tbody tr:nth-child(even) { background: #f8fafc; }
    .lead-table tbody tr:hover { background: #eaf2ff; }
  `;

  return (
    <div
      style={{
        maxWidth: 1500,
        margin: "0 auto",
        padding: 18,
        fontFamily: "Segoe UI, Arial, sans-serif",
        background: "linear-gradient(120deg,#f3f6fb 60%,#e0e7ef 100%)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 12,
          borderBottom: "2px solid #3b82f6",
          padding: "16px 18px",
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 8px 24px #2563eb0d",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <img
            src="/logo-capta.png"
            alt="Logo CaptaCRM"
            style={{ height: 70, objectFit: "contain" }}
          />
          <div>
            <div
              style={{
                color: "#2563eb",
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              Relatório para impressão
            </div>
            <div
              style={{
                color: "#0f172a",
                fontWeight: 800,
                fontSize: 30,
                lineHeight: 1.1,
              }}
            >
              Carteira de Leads
            </div>
            <div style={{ color: "#475569", fontWeight: 600, marginTop: 4 }}>
              {nomeClinica}
            </div>
          </div>
        </div>

        <div
          className="no-print"
          style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
        >
          <button
            type="button"
            onClick={exportarExcel}
            style={{
              background: "linear-gradient(90deg,#059669 60%,#34d399 100%)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              padding: "10px 20px",
              border: "none",
              borderRadius: 10,
              boxShadow: "0 8px 20px #0596692b",
              cursor: "pointer",
            }}
          >
            📁 Exportar Excel
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            style={{
              background: "linear-gradient(90deg,#3b82f6 60%,#1d4ed8 100%)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              padding: "10px 20px",
              border: "none",
              borderRadius: 10,
              boxShadow: "0 8px 20px #3b82f62b",
              cursor: "pointer",
            }}
          >
            🖨️ Imprimir
          </button>
        </div>
      </div>

      <div
        className="no-print"
        style={{
          display: "flex",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
          marginBottom: 14,
          padding: "12px 14px",
          background: "#ffffff",
          borderRadius: 12,
          border: "1px solid #dbe3ef",
          boxShadow: "0 4px 12px #2563eb08",
        }}
      >
        <div>
          <div
            style={{
              fontWeight: 700,
              color: "#2563eb",
              marginBottom: 5,
              fontSize: 13,
            }}
          >
            Organizar impressão por
          </div>
          <select
            value={ordenarPor}
            onChange={(e) => setOrdenarPor(e.target.value)}
            style={{
              minWidth: 220,
              height: 40,
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              padding: "0 10px",
              background: "#fff",
              color: "#222",
              fontWeight: 600,
            }}
          >
            {ordenacaoOpcoes.map((opcao) => (
              <option key={opcao.value} value={opcao.value}>
                {opcao.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div
            style={{
              fontWeight: 700,
              color: "#2563eb",
              marginBottom: 5,
              fontSize: 13,
            }}
          >
            Ordem
          </div>
          <select
            value={direcaoOrdem}
            onChange={(e) => setDirecaoOrdem(e.target.value)}
            style={{
              minWidth: 170,
              height: 40,
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              padding: "0 10px",
              background: "#fff",
              color: "#222",
              fontWeight: 600,
            }}
          >
            <option value="asc">Crescente</option>
            <option value="desc">Decrescente</option>
          </select>
        </div>
      </div>

      <div
        className="no-print"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <DropdownFiltro
          titulo="Procedimento"
          cor="#db2777"
          options={procedimentosOptions}
          selected={filtroProcedimento}
          onToggle={(e) =>
            handleFiltroMulti(e, filtroProcedimento, setFiltroProcedimento)
          }
          onClear={() => setFiltroProcedimento([])}
        />
        <DropdownFiltro
          titulo="Potencial"
          cor="#be185d"
          options={potencialOpcoes.map((v) => ({ value: v, label: v }))}
          selected={filtroPotencial}
          onToggle={(e) =>
            handleFiltroMulti(e, filtroPotencial, setFiltroPotencial)
          }
          onClear={() => setFiltroPotencial([])}
        />
        <DropdownFiltro
          titulo="Status"
          cor="#3b82f6"
          options={statusOpcoes.map((v) => ({ value: v, label: v }))}
          selected={filtroStatus}
          onToggle={(e) => handleFiltroMulti(e, filtroStatus, setFiltroStatus)}
          onClear={() => setFiltroStatus([])}
        />
        <DropdownFiltro
          titulo="Negociação"
          cor="#059669"
          options={negociacaoOpcoes.map((v) => ({ value: v, label: v }))}
          selected={filtroNegociacao}
          onToggle={(e) =>
            handleFiltroMulti(e, filtroNegociacao, setFiltroNegociacao)
          }
          onClear={() => setFiltroNegociacao([])}
        />
        <DropdownFiltro
          titulo="Região"
          cor="#a21caf"
          options={regioesOptions}
          selected={filtroRegiao}
          onToggle={(e) => handleFiltroMulti(e, filtroRegiao, setFiltroRegiao)}
          onClear={() => setFiltroRegiao([])}
        />
        <DropdownFiltro
          titulo="Dentista"
          cor="#7c3aed"
          options={dentistasOptions}
          selected={filtroDentista}
          onToggle={(e) =>
            handleFiltroMulti(e, filtroDentista, setFiltroDentista)
          }
          onClear={() => setFiltroDentista([])}
        />
        <DropdownFiltro
          titulo="Fonte"
          cor="#0ea5e9"
          options={fontesOptions}
          selected={filtroFonte}
          onToggle={(e) => handleFiltroMulti(e, filtroFonte, setFiltroFonte)}
          onClear={() => setFiltroFonte([])}
        />
        <DropdownFiltro
          titulo="Captador"
          cor="#f59e42"
          options={usuariosOptions}
          selected={filtroCaptador}
          onToggle={(e) =>
            handleFiltroMulti(e, filtroCaptador, setFiltroCaptador)
          }
          onClear={() => setFiltroCaptador([])}
        />
        <DropdownFiltro
          titulo="Campanha"
          cor="#ea580c"
          options={campanhasOptions}
          selected={filtroCampanha}
          onToggle={(e) =>
            handleFiltroMulti(e, filtroCampanha, setFiltroCampanha)
          }
          onClear={() => setFiltroCampanha([])}
        />

        <button
          type="button"
          onClick={() => {
            setFiltroProcedimento([]);
            setFiltroPotencial([]);
            setFiltroStatus([]);
            setFiltroNegociacao([]);
            setFiltroRegiao([]);
            setFiltroDentista([]);
            setFiltroFonte([]);
            setFiltroCaptador([]);
            setFiltroCampanha([]);
          }}
          style={{
            height: 42,
            borderRadius: 10,
            border: "1px solid #fca5a5",
            background: "#fff",
            color: "#dc2626",
            fontWeight: 800,
            padding: "0 14px",
            cursor: "pointer",
          }}
        >
          Limpar filtros
        </button>
      </div>

      <div
        style={{
          margin: "0 0 12px 0",
          padding: "12px 15px",
          background: "#ffffff",
          borderRadius: 12,
          border: "1px solid #dbe3ef",
          boxShadow: "0 4px 12px #2563eb08",
          color: "#222",
          fontSize: 14,
          fontWeight: 500,
        }}
      >
        <span style={{ color: "#1976d2", fontWeight: 800 }}>
          Filtros usados no relatório:
        </span>{" "}
        {!filtrosExibidos.length ? (
          <span style={{ color: "#94a3b8" }}>Sem filtros aplicados</span>
        ) : (
          filtrosExibidos.map((f, idx) => (
            <span key={f.label}>
              <span style={{ color: "#1976d2", fontWeight: 800 }}>
                {f.label}
              </span>
              <span style={{ color: "#222", fontWeight: 500 }}>
                {" "}
                {f.render ? f.render(f.value) : f.value.join(", ")}
              </span>
              {idx < filtrosExibidos.length - 1 ? (
                <span style={{ color: "#cbd5e1" }}> | </span>
              ) : null}
            </span>
          ))
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
          gap: 14,
          margin: "0 0 12px 0",
        }}
      >
        {[
          { icon: "👤", label: "Leads", value: totalLeads, color: "#3b82f6" },
          {
            icon: "💰",
            label: "Orçamento",
            value: moneyBRL(totalOrcamento),
            color: "#1d4ed8",
          },
          {
            icon: "✔️",
            label: "Contratado",
            value: moneyBRL(totalContratado),
            color: "#059669",
          },
        ].map((card) => (
          <div
            key={card.label}
            style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 6px 16px #2563eb0f",
              border: `1px solid ${card.color}33`,
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                background: card.color,
                color: "#fff",
                borderRadius: 8,
                padding: 7,
                fontWeight: 700,
                fontSize: 18,
                minWidth: 36,
                textAlign: "center",
              }}
            >
              {card.icon}
            </span>
            <div>
              <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>
                {card.label}
              </div>
              <div style={{ fontWeight: 800, fontSize: 16, color: card.color }}>
                {card.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div
        className="no-print"
        style={{
          background: "#ffffff",
          marginBottom: 12,
          padding: 12,
          borderRadius: 12,
          border: "1px solid #dbe3ef",
          boxShadow: "0 4px 12px #2563eb08",
        }}
      >
        <b style={{ color: "#1e293b", fontSize: 13 }}>Campos para exibir:</b>
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}
        >
          {camposDisponiveis.map((campo) => (
            <label
              key={campo.value}
              style={{
                minWidth: 110,
                color: "#334155",
                fontSize: 12,
                fontWeight: 600,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 999,
                padding: "7px 10px",
              }}
            >
              <input
                type="checkbox"
                value={campo.value}
                checked={camposSelecionados.includes(campo.value)}
                onChange={handleCampoChange}
                style={{ accentColor: "#3b82f6", marginRight: 4 }}
              />{" "}
              {campo.label}
            </label>
          ))}
        </div>
      </div>

      <div
        className="tabela-scroll"
        style={{
          overflowX: "scroll",
          overflowY: "scroll",
          maxWidth: "100vw",
          minHeight: 300,
          maxHeight: "58vh",
          borderRadius: 12,
          border: "1px solid #dbe3ef",
          background: "#fff",
          boxShadow: "0 6px 16px #2563eb0f",
          marginBottom: 25,
        }}
      >
        <div id="print-area">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              gap: 12,
              flexWrap: "wrap",
              padding: "0 0 12px 0",
            }}
          >
            <div>
              <div
                style={{
                  color: "#2563eb",
                  fontWeight: 800,
                  fontSize: 11,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Relatório de Leads
              </div>
              <div style={{ color: "#0f172a", fontWeight: 800, fontSize: 22 }}>
                {nomeClinica}
              </div>
            </div>

            <div style={{ color: "#475569", fontSize: 12, fontWeight: 600 }}>
              Ordenado por{" "}
              <span style={{ color: "#1d4ed8" }}>
                {ordenacaoOpcoes.find((o) => o.value === ordenarPor)?.label ||
                  "Paciente"}
              </span>{" "}
              ({direcaoOrdem === "asc" ? "Crescente" : "Decrescente"})
            </div>
          </div>

          <table
            className="lead-table"
            style={{
              borderCollapse: "collapse",
              width: "100%",
              minWidth: 980,
              fontSize: 13,
              background: "#fff",
              fontWeight: 500,
            }}
          >
            <thead>
              <tr>
                {camposDisponiveis
                  .filter((c) => camposSelecionados.includes(c.value))
                  .map((campo) => (
                    <th
                      key={campo.value}
                      style={{
                        background: "#1d4ed8",
                        color: "#fff",
                        fontWeight: "bold",
                        padding: "10px 8px",
                        border: "1px solid #e0e7ef",
                        fontSize: 12,
                        textAlign: "left",
                        letterSpacing: 0.3,
                        textTransform: "uppercase",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {campo.label}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {leadsOrdenadas.length === 0 ? (
                <tr>
                  <td
                    colSpan={camposSelecionados.length}
                    style={{
                      textAlign: "center",
                      color: "#94a3b8",
                      padding: 22,
                      fontSize: 16,
                    }}
                  >
                    Nenhuma lead encontrada.
                  </td>
                </tr>
              ) : (
                leadsOrdenadas.map((lead, idx) => (
                  <tr key={lead.id || `${idx}-${lead.nome_lead || "lead"}`}>
                    {camposDisponiveis
                      .filter((c) => camposSelecionados.includes(c.value))
                      .map((campo) => (
                        <td
                          key={campo.value}
                          style={{
                            border: "1px solid #e0e7ef",
                            padding: "8px 9px",
                            background: "#fff",
                            color: "#222",
                            verticalAlign: "top",
                          }}
                        >
                          {renderCell(lead, campo.value)}
                        </td>
                      ))}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="totais-footer">
              <tr>
                <td
                  colSpan={camposSelecionados.length}
                  style={{
                    padding: 12,
                    background: "#f8fafc",
                    borderTop: "2px solid #3b82f6",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#0f172a",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 20,
                      fontSize: 13,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span>
                      👤 <span style={{ color: "#334155" }}>Total leads:</span>{" "}
                      <b>{totalLeads}</b>
                    </span>
                    <span>
                      💰 <span style={{ color: "#334155" }}>Orçamento:</span>{" "}
                      <b>{moneyBRL(totalOrcamento)}</b>
                    </span>
                    <span>
                      ✔️ <span style={{ color: "#334155" }}>Contratado:</span>{" "}
                      <b>{moneyBRL(totalContratado)}</b>
                    </span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <style>{printCss}</style>
    </div>
  );
}
